import { useEffect, useRef, useState } from 'react'
import { useSearchParams, type SetURLSearchParams } from 'react-router'
import { PRODUCT_TYPES } from 'services/ProductTypeState'
import { useAuth } from 'services/AuthProvider/AuthProvider'
import { analytics, EVENT_NAMES } from '../../Analytics/Analytics'
import { logger } from '../../logger'
import { AbandonedCartItemSchema } from './abandonedCart.schemas'
import { FLOWS } from '../userCart.consts'
import { clearCartRestoreParam, restoreCart } from '../userCartService'
import { updateTilesFromUserCart } from '../tilesUserCart'
import { updateBooksFromUserCart } from '../bookUserCart'
import {
  getPhotoBankStore,
  getPhotoBooksStore,
  getTilesStore,
} from 'stores/RootStoreProvider'
import { UPLOAD_SRC } from 'components/PhotoBank/PhotoBank.consts'
import { saveMaterialType } from 'utils/materialTypeUtils'
import { setSelectedSize } from 'hooks/tileStyling/utils'

export function useAbandonedCartRestore(): boolean {
  const [, setSearchParams] = useSearchParams()
  const { isLoading: isAuthLoading } = useAuth()
  const [isRestoreComplete, setIsRestoreComplete] = useState(false)
  const hasStartedRestoreRef = useRef(false)

  // A guest's first auth answer reaches the stores as a sign-out, which discards
  // any book still being received, so the restore starts only once auth has answered.
  useEffect(() => {
    if (isAuthLoading || hasStartedRestoreRef.current) return
    hasStartedRestoreRef.current = true
    Promise.all([
      updateTilesFromUserCart(setSearchParams),
      updateBooksFromUserCart(setSearchParams),
      updateBagFromUserCart(setSearchParams),
    ]).finally(() => setIsRestoreComplete(true))
  }, [isAuthLoading, setSearchParams])

  return isRestoreComplete
}

export async function updateBagFromUserCart(
  setSearchParams: SetURLSearchParams
): Promise<void> {
  const { cartId, userCart } = await restoreCart(FLOWS.CART)
  if (!userCart) return
  clearCartRestoreParam(FLOWS.CART, setSearchParams)

  let restoredItemsCount = 0
  for (const rawItem of userCart.cartItems ?? []) {
    const parsedItem = AbandonedCartItemSchema.safeParse(rawItem)
    if (!parsedItem.success) {
      logger.error('Invalid abandoned cart item', parsedItem.error)
      continue
    }
    const item = parsedItem.data
    try {
      if (item.productType === PRODUCT_TYPES.CLASSIC) {
        const { tiles, photos, bulkOptions } = item.restorePayload
        // Tiles resolve their photo from the bank by uid, so it has to hold them
        // before the store is asked to build the tiles.
        getPhotoBankStore().appendPhotos({
          photos,
          uploadSrc: UPLOAD_SRC.TILE_DESIGNER,
        })
        getTilesStore().restoreTiles(tiles)
        setSelectedSize(bulkOptions.tileSize)
        saveMaterialType(bulkOptions.materialType)
        restoredItemsCount++
      } else if (item.productType === PRODUCT_TYPES.PHOTO_BOOK) {
        const { book, photos } = item.restorePayload
        getPhotoBankStore().appendPhotos({
          photos,
          uploadSrc: UPLOAD_SRC.PHOTO_BOOK,
        })
        await getPhotoBooksStore().receiveExternalBook(book, 'User Cart')
        restoredItemsCount++
      }
    } catch (error) {
      logger.error('Failed to restore abandoned cart item', error)
    }
  }

  analytics.track(EVENT_NAMES.CART_RESTORED, {
    Flow: FLOWS.CART,
    'Restored Items Count': restoredItemsCount,
    'Restored Cart ID': cartId,
  })
}
